export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown,
    // Скільки мс почекати перед ретраєм, коли сервер так попросив (HTTP
    // `Retry-After` для 429/503). null — клієнт сам обирає backoff.
    public readonly retryAfterMs: number | null = null,
  ) {
    super(`API ${status}: ${code}`);
    this.name = 'ApiError';
  }
}

const API_BASE = '/api';

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // fetch() кидає TypeError на мережевих збоях (DNS, offline, CORS, TLS,
  // обрив тунелю) — *до* того, як зʼявиться Response. Нормалізуємо в
  // ApiError(0) щоб ретраї та UI обробляли це уніфіковано з XHR-помилками
  // чанка (там xhr.onerror теж дає status 0 / 'network_error').
  // AbortError від AbortController сюди не потрапляє — це DOMException, не TypeError.
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError) {
      throw new ApiError(0, 'network_error', null);
    }
    throw err;
  }

  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const code =
      typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string'
        ? body.error
        : 'http_error';
    throw new ApiError(res.status, code, body);
  }

  // 204 No Content (например DELETE): тела нет, res.json() на пустом ответе падает.
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

export async function apiJson<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', ...init?.headers},
    body: JSON.stringify(body),
    ...init,
  });
}
